import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { FolderOpen } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { HeroSection } from '@/sections/HeroSection';
import { EvidencePackSection } from '@/sections/EvidencePackSection';
import { ContextChipSection } from '@/sections/ContextChipSection';
import { QuoteDetailSection } from '@/sections/QuoteDetailSection';
import { ShareCardSection } from '@/sections/ShareCardSection';
import { FeaturesSection } from '@/sections/FeaturesSection';
import { ClosingCTASection } from '@/sections/ClosingCTASection';
import { SearchOverlay } from '@/components/search/SearchOverlay';
import { ResultsView } from '@/components/search/ResultsView';
import { QuoteDetailModal } from '@/components/quote/QuoteDetailModal';
import { ShareCardModal } from '@/components/quote/ShareCardModal';
import { TrailsDrawer } from '@/components/trail/TrailsDrawer';
import { MobileLanding } from '@/components/MobileLanding';
import { Toast } from '@/components/Toast';
import { useSearch } from '@/hooks/useSearch';
import { useTrails } from '@/hooks/useTrails';
import { useToast } from '@/hooks/useToast';
import { useIsMobile } from '@/hooks/use-mobile';
import type { LegacyQuoteCard, Trail } from '@/types';
import './App.css';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

function App() {
  const mainRef = useRef<HTMLElement>(null);
  const scrollTriggersRef = useRef<ScrollTrigger[]>([]);
  const isMobile = useIsMobile();

  // Search state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const { query, results, sources, answer, isSearching, error, search, clearSearch } = useSearch();

  // Trails state
  const [isTrailsOpen, setIsTrailsOpen] = useState(false);
  const [pendingCard, setPendingCard] = useState<LegacyQuoteCard | null>(null);
  const {
    trails,
    activeTrail,
    setActiveTrail,
    createTrail,
    addToTrail,
    removeFromTrail,
    deleteTrail,
  } = useTrails({ demo: true });

  // Quote detail state
  const [selectedCard, setSelectedCard] = useState<LegacyQuoteCard | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Share state
  const [shareCard, setShareCard] = useState<LegacyQuoteCard | null>(null);
  const [isShareOpen, setIsShareOpen] = useState(false);

  // Toast
  const { showToast, toast, hideToast } = useToast();

  // Global snap for pinned sections
  useEffect(() => {
    const timeout = setTimeout(() => {
      const pinned = ScrollTrigger.getAll()
        .filter((st) => st.vars.pin)
        .sort((a, b) => a.start - b.start);

      const maxScroll = ScrollTrigger.maxScroll(window);

      if (!maxScroll || pinned.length === 0) return;

      const pinnedRanges = pinned.map((st) => ({
        start: st.start / maxScroll,
        end: (st.end ?? st.start) / maxScroll,
        center: (st.start + ((st.end ?? st.start) - st.start) * 0.5) / maxScroll,
      }));

      const globalSnap = ScrollTrigger.create({
        snap: {
          snapTo: (value: number) => {
            const inPinned = pinnedRanges.some(
              (r) => value >= r.start - 0.02 && value <= r.end + 0.02
            );

            if (!inPinned) return value;

            const target = pinnedRanges.reduce(
              (closest, r) =>
                Math.abs(r.center - value) < Math.abs(closest - value) ? r.center : closest,
              pinnedRanges[0]?.center ?? 0
            );

            return target;
          },
          duration: { min: 0.15, max: 0.35 },
          delay: 0,
          ease: 'power2.out',
        },
      });

      scrollTriggersRef.current.push(globalSnap);
    }, 500);

    return () => {
      clearTimeout(timeout);
      scrollTriggersRef.current.forEach((st) => st.kill());
      scrollTriggersRef.current = [];
    };
  }, [isMobile]);

  // Search handlers
  const handleStartSearch = () => {
    setIsSearchOpen(true);
  };

  const handleSearch = (searchQuery: string) => {
    search(searchQuery);
    setIsSearchOpen(false);
    setTimeout(() => setIsResultsOpen(true), 300);
  };

  const handleCloseResults = () => {
    setIsResultsOpen(false);
    clearSearch();
  };

  // Card handlers
  const handleViewDetail = (card: LegacyQuoteCard) => {
    setSelectedCard(card);
    setIsDetailOpen(true);
    setIsResultsOpen(false);
  };

  const handleCloseDetail = () => {
    setIsDetailOpen(false);
    setTimeout(() => setSelectedCard(null), 300);
  };

  // Trail handlers
  const handleAddToTrail = async (card: LegacyQuoteCard) => {
    try {
      if (activeTrail) {
        await addToTrail(activeTrail.id, card);
        showToast(`Added to "${activeTrail.name}"`, 'success');
      } else if (trails.length > 0) {
        setPendingCard(card);
        setIsTrailsOpen(true);
        showToast('Choose a trail for this receipt', 'info');
      } else {
        const newTrail = await createTrail('My Research');
        await addToTrail(newTrail.id, card);
        setActiveTrail(newTrail);
        showToast('Created new trail and added card', 'success');
      }
    } catch {
      showToast('Unable to save this receipt', 'error');
    }
  };

  const handleCreateTrail = async (name: string): Promise<Trail> => {
    const newTrail = await createTrail(name);
    setActiveTrail(newTrail);

    if (pendingCard) {
      await addToTrail(newTrail.id, pendingCard);
      setPendingCard(null);
      showToast(`Added to "${newTrail.name}"`, 'success');
    }

    return newTrail;
  };

  const handleSavePending = async (trail: Trail) => {
    if (!pendingCard) return;

    try {
      await addToTrail(trail.id, pendingCard);
      setActiveTrail(trail);
      setPendingCard(null);
      setIsTrailsOpen(false);
      showToast(`Added to "${trail.name}"`, 'success');
    } catch {
      showToast('Unable to save this receipt', 'error');
    }
  };

  const handleRemoveFromTrail = async (trailId: string, cardId: string) => {
    try {
      await removeFromTrail(trailId, cardId);
      showToast('Card removed from trail', 'info');
    } catch {
      showToast('Unable to remove this receipt', 'error');
    }
  };

  const handleDeleteTrail = async (trailId: string) => {
    try {
      await deleteTrail(trailId);
      showToast('Trail deleted', 'info');
    } catch {
      showToast('Unable to delete this trail', 'error');
    }
  };

  const handleExportTrail = (trail: Trail) => {
    // Export as JSON
    const data = JSON.stringify(trail, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${trail.name.replace(/\s+/g, '-').toLowerCase()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    showToast('Trail exported', 'success');
  };

  // Share handlers
  const handleShare = (card: LegacyQuoteCard) => {
    setShareCard(card);
    setIsShareOpen(true);
    setIsResultsOpen(false);
    setIsDetailOpen(false);
  };

  const handleCloseShare = () => {
    setIsShareOpen(false);
    setTimeout(() => setShareCard(null), 300);
  };

  const savedCardIds = trails.flatMap((t) => t.cards.map((c) => c.id));

  return (
    <div className="paper-texture">
      <Navigation />

      {/* Trails Button (Floating) */}
      <button
        onClick={() => setIsTrailsOpen(true)}
        className="fixed bottom-4 right-4 z-[150] flex items-center gap-2 rounded-full bg-[#0B0D10] px-4 py-3 text-white shadow-lg transition-colors hover:bg-[#1a1d23] md:bottom-8 md:right-8"
      >
        <FolderOpen className="h-5 w-5" />
        <span className="font-medium">Trails</span>
        {savedCardIds.length > 0 && (
          <span className="ml-1 rounded-full bg-[#E1062C] px-2 py-0.5 text-xs">
            {savedCardIds.length}
          </span>
        )}
      </button>

      <main ref={mainRef} className="relative">
        {isMobile ? (
          <MobileLanding onStartSearch={handleStartSearch} />
        ) : (
          <>
            {/* Section 1: Hero - pin: true */}
            <div data-section="hero">
              <HeroSection
                onStartSearch={handleStartSearch}
                onSearch={handleSearch}
                onSeeDemo={() => {
                  gsap.to(window, {
                    scrollTo: { y: window.innerHeight, autoKill: false },
                    duration: 1,
                    ease: 'power2.inOut',
                  });
                }}
              />
            </div>

            {/* Section 2: Evidence Pack - pin: true */}
            <div data-section="evidence">
              <EvidencePackSection />
            </div>

            {/* Section 3: Context Chip - pin: true */}
            <div data-section="context">
              <ContextChipSection />
            </div>

            {/* Section 4: Quote Detail - pin: true */}
            <div data-section="detail">
              <QuoteDetailSection />
            </div>

            {/* Section 5: Share Card - pin: true */}
            <div data-section="share">
              <ShareCardSection />
            </div>

            {/* Section 6: Features - pin: false */}
            <FeaturesSection />

            {/* Section 7: Closing CTA - pin: false */}
            <ClosingCTASection />
          </>
        )}
      </main>

      {/* Search Overlay */}
      <SearchOverlay
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSearch={handleSearch}
        isSearching={isSearching}
      />

      {/* Results View */}
      <ResultsView
        isOpen={isResultsOpen}
        onClose={handleCloseResults}
        query={query}
        results={results}
        sources={sources}
        answer={answer}
        isSearching={isSearching}
        error={error}
        onViewDetail={handleViewDetail}
        onAddToTrail={handleAddToTrail}
        onShare={handleShare}
        savedCardIds={savedCardIds}
      />

      {/* Quote Detail Modal */}
      <QuoteDetailModal
        card={selectedCard}
        isOpen={isDetailOpen}
        onClose={handleCloseDetail}
        isSaved={selectedCard ? savedCardIds.includes(selectedCard.id) : false}
        onAddToTrail={() => selectedCard && handleAddToTrail(selectedCard)}
        onShare={() => selectedCard && handleShare(selectedCard)}
      />

      {/* Share Card Modal */}
      <ShareCardModal
        card={shareCard}
        searchQuery={query}
        isOpen={isShareOpen}
        onClose={handleCloseShare}
      />

      {/* Trails Drawer */}
      <TrailsDrawer
        isOpen={isTrailsOpen}
        onClose={() => setIsTrailsOpen(false)}
        trails={trails}
        activeTrail={activeTrail}
        pendingCard={pendingCard}
        onCreateTrail={handleCreateTrail}
        onSelectTrail={setActiveTrail}
        onSavePending={handleSavePending}
        onRemoveFromTrail={handleRemoveFromTrail}
        onDeleteTrail={handleDeleteTrail}
        onExportTrail={handleExportTrail}
      />

      {/* Toast */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
}

export default App;
